import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguageManually } from '../i18n';

import './LanguageSwitcher.css';

const LANGUAGES = [
  {
    code: 'ru',
    short: 'RU',
    label: 'Русский',
    flag: '/img/flags/ru.png'
  },
  {
    code: 'en',
    short: 'EN',
    label: 'English',
    flag: '/img/flags/gb.png'
  },
  {
    code: 'ka',
    short: 'KA',
    label: 'ქართული',
    flag: '/img/flags/ge.png'
  },
  {
    code: 'hy',
    short: 'HY',
    label: 'Հայերեն',
    flag: '/img/flags/am.png'
  },
  {
    code: 'az',
    short: 'AZ',
    label: 'Azərbaycan',
    flag: '/img/flags/az.png'
  },
  {
    code: 'it',
    short: 'IT',
    label: 'Italiano',
    flag: '/img/flags/it.png'
  },
  {
    code: 'fr',
    short: 'FR',
    label: 'Français',
    flag: '/img/flags/fr.png'
  }
];

function normalizeLanguageCode(language) {
  return String(language || 'ru')
    .trim()
    .toLowerCase()
    .split('-')[0];
}

export default function LanguageSwitcher({
  compact = false
}) {
  const { t, i18n } = useTranslation();

  const rootRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  /*
   * Держим выбранный язык ещё и в локальном state.
   * Поэтому надпись RU / EN / IT меняется сразу после клика,
   * даже если какой-то экран не успел перерисоваться от i18next.
   */
  const [selectedLanguageCode, setSelectedLanguageCode] =
    useState(() =>
      normalizeLanguageCode(
        i18n.resolvedLanguage ||
        i18n.language ||
        'ru'
      )
    );

  /*
   * Синхронизируем локальный state с i18next.
   * Это важно, если язык меняется не этим компонентом,
   * а, например, автоматически по стране/IP.
   */
  useEffect(() => {
    const syncLanguage = (language) => {
      setSelectedLanguageCode(
        normalizeLanguageCode(language)
      );
    };

    syncLanguage(
      i18n.resolvedLanguage ||
      i18n.language ||
      'ru'
    );

    i18n.on('languageChanged', syncLanguage);

    return () => {
      i18n.off('languageChanged', syncLanguage);
    };
  }, [i18n]);

  const currentLanguage = useMemo(() => {
    return (
      LANGUAGES.find(
        (language) =>
          language.code === selectedLanguageCode
      ) || LANGUAGES[0]
    );
  }, [selectedLanguageCode]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!rootRef.current) return;

      if (!rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    document.addEventListener(
      'keydown',
      handleEscape
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );

      document.removeEventListener(
        'keydown',
        handleEscape
      );
    };
  }, []);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelectLanguage = async (
    languageCode
  ) => {
    const normalizedLanguageCode =
      normalizeLanguageCode(languageCode);

    const languageExists = LANGUAGES.some(
      (language) =>
        language.code === normalizedLanguageCode
    );

    if (!languageExists) {
      console.error(
        '[LanguageSwitcher] Неподдерживаемый язык:',
        languageCode
      );
      return;
    }

    /*
     * Сразу меняем подпись самого переключателя.
     * Пользователь моментально увидит EN / English и т.д.
     */
    setSelectedLanguageCode(
      normalizedLanguageCode
    );

    setIsOpen(false);

    try {
      /*
       * Твой существующий helper:
       * - записывает bgq_language в localStorage;
       * - вызывает i18n.changeLanguage().
       */
      await setLanguageManually(
        normalizedLanguageCode
      );

      /*
       * Дополнительная страховка.
       * Обычно этот блок уже не понадобится,
       * но он не даст i18next остаться на старом языке.
       */
      const actualLanguage =
        normalizeLanguageCode(
          i18n.resolvedLanguage ||
          i18n.language
        );

      if (
        actualLanguage !==
        normalizedLanguageCode
      ) {
        await i18n.changeLanguage(
          normalizedLanguageCode
        );
      }

      console.log(
        '[LanguageSwitcher] language changed:',
        normalizedLanguageCode,
        i18n.language,
        i18n.resolvedLanguage
      );
    } catch (error) {
      console.error(
        '[LanguageSwitcher] Ошибка смены языка:',
        error
      );

      /*
       * Если смена реально не удалась,
       * возвращаем переключатель к фактическому языку i18next.
       */
      setSelectedLanguageCode(
        normalizeLanguageCode(
          i18n.resolvedLanguage ||
          i18n.language ||
          'ru'
        )
      );
    }
  };

  return (
    <div
      ref={rootRef}
      className={
        'language-switcher' +
        (compact
          ? ' language-switcher--compact'
          : '') +
        (isOpen ? ' is-open' : '')
      }
    >
      <button
        type="button"
        className="language-switcher__trigger"
        onClick={handleToggle}
        aria-label={t(
          'language.title',
          'Язык'
        )}
        aria-expanded={isOpen}
      >
        <img
          src={currentLanguage.flag}
          alt=""
          aria-hidden="true"
          className="language-switcher__flag-img"
        />

        <span className="language-switcher__value">
          {currentLanguage.short}
        </span>

        <span className="language-switcher__name">
          {currentLanguage.label}
        </span>

        <span className="language-switcher__arrow">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="language-switcher__menu">
          {LANGUAGES.map((language) => {
            const active =
              language.code ===
              currentLanguage.code;

            return (
              <button
                key={language.code}
                type="button"
                className={
                  'language-switcher__option' +
                  (active ? ' is-active' : '')
                }
                onClick={() =>
                  handleSelectLanguage(
                    language.code
                  )
                }
              >
                <img
                  src={language.flag}
                  alt=""
                  aria-hidden="true"
                  className="language-switcher__option-flag-img"
                />

                <span className="language-switcher__option-short">
                  {language.short}
                </span>

                <span className="language-switcher__option-label">
                  {language.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
